import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Checkbox, ContextualMenu, ContextualMenuItemType, DirectionalHint, Icon, IContextualMenuItem, Text } from '@fluentui/react';
import './SearchInDropdown.scss';
import { useTranslation } from 'react-i18next';
import Button from '../button/Button';
import { SearchInField } from 'app/models/common/enumerators';

const BACKEND_FIELD_SUMMARY = 'Summary';
const BACKEND_FIELD_DESCRIPTION = 'Description';
const BACKEND_FIELD_TRACKING_ID = 'UnifiedTrackingId';

export interface ISearchInOption {
    key: SearchInField;
    label: string;
}

const NON_SENTINEL_OPTIONS: ISearchInOption[] = [
    { key: SearchInField.Title, label: 'Title' },
    { key: SearchInField.Description, label: 'Description' },
    { key: SearchInField.TicketNumber, label: 'Ticket number' }
];

const ALL_FIELDS_OPTION: ISearchInOption = { key: SearchInField.AllFields, label: 'All fields' };

const FIELD_TO_BACKEND: Record<Exclude<SearchInField, SearchInField.AllFields>, { search: string; highlight?: string }> = {
    [SearchInField.Title]: { search: BACKEND_FIELD_SUMMARY, highlight: BACKEND_FIELD_SUMMARY },
    [SearchInField.Description]: { search: BACKEND_FIELD_DESCRIPTION, highlight: BACKEND_FIELD_DESCRIPTION },
    [SearchInField.TicketNumber]: { search: BACKEND_FIELD_TRACKING_ID }
};

export interface SearchFieldParams {
    searchField: string;
    customSearchFields: string[];
    highlightFields: string[];
}

/**
 * Maps the selected SearchInField array to the backend searchField / customSearchFields / highlightFields.
 * SearchInField.AllFields (or an empty/undefined selection) expands to every non-sentinel field.
 */
export const getSearchFieldParams = (values: SearchInField[] | SearchInField | undefined) => {
    const arr: SearchInField[] = Array.isArray(values)
        ? values
        : values
            ? [values]
            : [SearchInField.AllFields];

    const isAll = arr.length === 0 || arr.includes(SearchInField.AllFields);
    const effective: Array<Exclude<SearchInField, SearchInField.AllFields>> = isAll
        ? (NON_SENTINEL_OPTIONS.map(o => o.key) as Array<Exclude<SearchInField, SearchInField.AllFields>>)
        : (arr.filter(v => v !== SearchInField.AllFields) as Array<Exclude<SearchInField, SearchInField.AllFields>>);

    const customSearchFields = effective.map(v => FIELD_TO_BACKEND[v].search);
    const highlightFields = effective
        .map(v => FIELD_TO_BACKEND[v].highlight)
        .filter((h): h is string => !!h);

    return {
        searchField: 'Custom',
        customSearchFields,
        highlightFields
    };
};

interface ISearchInDropdownProps {
    values: SearchInField[];
    onChange: (values: SearchInField[]) => void;
    className?: string;
}

const arraysEqualAsSets = (a: SearchInField[], b: SearchInField[]) => {
    if (a.length !== b.length) return false;
    const setB = new Set(b);
    return a.every(v => setB.has(v));
};

const normalize = (values: SearchInField[]): SearchInField[] => {
    if (!values || values.length === 0) return [SearchInField.AllFields];
    if (values.includes(SearchInField.AllFields)) return [SearchInField.AllFields];
    const allNonSentinelSelected = NON_SENTINEL_OPTIONS.every(o => values.includes(o.key));
    if (allNonSentinelSelected) return [SearchInField.AllFields];
    return values;
};

const SearchInDropdown: React.FC<ISearchInDropdownProps> = ({ values, onChange, className }) => {
    const { t: translate } = useTranslation();
    const [showMenu, setShowMenu] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);

    const selected = useMemo(() => normalize(values || [SearchInField.AllFields]), [values]);
    const isAllSelected = selected.includes(SearchInField.AllFields);

    const triggerLabel = useMemo(() => {
        if (isAllSelected) return translate(ALL_FIELDS_OPTION.label);
        const names = NON_SENTINEL_OPTIONS
            .filter(o => selected.includes(o.key))
            .map(o => translate(o.label));
        if (names.length === 0) return translate(ALL_FIELDS_OPTION.label);
        if (names.length === 1) return names[0];
        return `${names[0]} +${names.length - 1}`;
    }, [selected, isAllSelected, translate]);

    const commit = useCallback(
        (next: SearchInField[]) => {
            const normalized = normalize(next);
            if (!arraysEqualAsSets(normalized, selected)) {
                onChange(normalized);
            }
        },
        [onChange, selected]
    );

    const toggleField = useCallback(
        (key: SearchInField) => {
            if (key === SearchInField.AllFields) {
                // Clicking "All fields" always resets to the sentinel-only selection.
                commit([SearchInField.AllFields]);
                return;
            }
            // Drop the sentinel if it was active, then toggle membership of the clicked field.
            const base = selected.filter(v => v !== SearchInField.AllFields);
            const next = base.includes(key) ? base.filter(v => v !== key) : [...base, key];
            commit(next);
        },
        [commit, selected]
    );

    const menuItems: IContextualMenuItem[] = useMemo(() => {
        const buildItem = (option: ISearchInOption): IContextualMenuItem => {
            const checked = selected.includes(option.key) || (isAllSelected && option.key !== SearchInField.AllFields);
            return {
                key: option.key,
                text: translate(option.label),
                canCheck: false,
                onRender: () => (
                    <button
                        type="button"
                        className="search-in-menu-item"
                        role="menuitemcheckbox"
                        aria-checked={checked}
                        onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            toggleField(option.key);
                        }}
                    >
                        <Checkbox
                            checked={checked}
                            styles={{ root: { pointerEvents: 'none' } }}
                        />
                        <span className="search-in-menu-item-text">{translate(option.label)}</span>
                    </button>
                )
            };
        };

        return [
            buildItem(ALL_FIELDS_OPTION),
            { key: 'divider', itemType: ContextualMenuItemType.Divider },
            ...NON_SENTINEL_OPTIONS.map(buildItem)
        ];
    }, [selected, toggleField, translate]);

    const openMenu = () => setShowMenu(true);
    const closeMenu = () => {
        setShowMenu(false);
        // Sentinel auto-reselect on close: never leave the user in a 0-selection dead end.
        if (!values || values.length === 0) {
            onChange([SearchInField.AllFields]);
        }
    };

    return (
        <div className={`search-in-dropdown ${className || ''}`}>
            <div
                ref={triggerRef}
                className={`search-in-trigger${isAllSelected ? '' : ' field-active'}`}
                tabIndex={0}
                role="button"
                aria-haspopup="menu"
                aria-expanded={showMenu}
                aria-label={`${translate('Search in')}: ${triggerLabel}`}
                onClick={() => (showMenu ? closeMenu() : openMenu())}
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        showMenu ? closeMenu() : openMenu();
                    }
                }}>
                <Text className="search-in-label">{translate('Search in')}:</Text>
                <Text className="search-in-value">{triggerLabel}</Text>
                <Icon iconName={showMenu ? 'ChevronUp' : 'ChevronDown'} className="search-in-chevron" />
            </div>
            <ContextualMenu
                items={menuItems}
                hidden={!showMenu}
                target={triggerRef.current}
                onDismiss={closeMenu}
                directionalHint={DirectionalHint.bottomLeftEdge}
                shouldFocusOnMount={true}
                className="search-in-menu"
                ariaLabel={translate('Search in field options')}
            />
        </div>
    );
};

export default SearchInDropdown;
